<template>
  <div class="add-client-modal">
    <app-loader v-if="isLoading" :style="loaderStyle" />
    <div class="add-client-modal__search">
      <app-input
        ref="$input"
        v-model="filter"
        class="add-client-modal__search-input"
        :placeholder="$t('search_short')"
        show-add-button
        :add-hint-text="$t('create_and_add_list')"
        @add="onAddElementToNewList"
        @press-enter="enter"
      />
      <div v-if="props.showCloseIcon" class="add-client-modal__close">
        <app-icon
          class="create-edit-contact__body-tags-item-delete"
          name="close"
          width="20px"
          color="#D2D2D2"
          hover-color="#499259"
          cursor="pointer"
          :title="$t('close_label')"
          @click="emit('close')"
        />
      </div>
    </div>
    <div class="add-client-modal__header">
      <div class="add-client-modal__header-title">{{ $t('available_lists') }}</div>
      <div class="add-client-modal__header-drop-btn" @click="dropAllLists">
        {{ $t('selector_reset') }}
      </div>
    </div>
    <div
      v-if="filteredLists?.length"
      class="add-client-modal__body"
      ref="$body"
    >
      <div
        v-for="element in filteredLists"
        class="add-client-modal__body-item"
        :key="element.Id"
      >
        <app-checkbox
          :style="{ height: '16px', marginRight: '8px' }"
          :value="isListAdded(element.Id)"
          text=""
          @input="onChangeListStage($event, element.Id)"
        />
        <div
          class="add-client-modal__body-item-text"
          @click="onChangeClientStageFromName(element.Id)"
          v-html="highlightText(element.Name)"
        ></div>
      </div>
    </div>
    <div class="add-client-modal__body-empty" v-else>
      <div class="add-client-modal__body-item-text">{{ $t('list_not_found') }}</div>
    </div>
    <!-- </div> -->
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import { clientsStore } from "@/store/clients";
import { debounce } from "lodash";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { AnalyticsApi } from "@/ api/AnalyticsApi";

type AnalyticsList = {
  Id: number;
  Name: string;
  Position: number;
};

type Props = {
  id: number;
  entityType?: number;
  addedLists: any[];
  showCloseIcon?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  entityType: 4
});

const emit = defineEmits(["close", "addList"]);

const $input = ref<typeof AppInput | null>(null);
const $body = ref<HTMLDivElement | null>(null);

const isLoading = ref(true);
const fileId = ref<number | null>(null);
const filter = ref("");
const take = 60;
const skip = ref(0);
const canLoadNext = ref(true);
const isListInit = ref(false);
const lists = ref<AnalyticsList[] | null>(null);
const existLists = computed(() => props.addedLists ?? []);

const filteredLists = computed(() => {
  if (!lists.value) return [];

  let result: AnalyticsList[] = [];

  if (existLists.value.length) {
    const sorted = [...existLists.value];

    lists.value.forEach((item) => {
      if (!existLists.value.find((c) => c.Id === item.Id)) {
        sorted.push(item);
      }
    });

    result = sorted;
  } else {
    result = [...lists.value];
  }

  result.sort((a, b) =>
    a.Name.localeCompare(b.Name, "ru", { sensitivity: "base" }),
  );

  if (!filter.value) return result;

  const search = filter.value.toLowerCase();

  return result.filter((item) => item.Name.toLowerCase().includes(search));
});

const loaderStyle = computed(() => ({
  "background-color": "rgba(255, 255, 255, 0.5)",
}));

const highlightText = (text: string) => {
  if (!filter.value) return escapeHtml(text);

  const lowerText = text.toLowerCase();
  const lowerSearch = filter.value.toLowerCase();

  const startIndex = lowerText.indexOf(lowerSearch);

  if (startIndex === -1) {
    return escapeHtml(text);
  }

  const endIndex = startIndex + filter.value.length;

  const before = text.substring(0, startIndex);
  const match = text.substring(startIndex, endIndex);
  const after = text.substring(endIndex);

  return `${escapeHtml(
    before,
  )}<span style="background-color:#D3E3FD;border-radius:4px;padding:1px;">${escapeHtml(
    match,
  )}</span>${escapeHtml(after)}`;
};

const escapeHtml = (text: string): string => {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};

const enter = () => {
  if (
    !existLists.value.includes(filteredLists.value[0]) &&
    filter.value &&
    filteredLists.value.length !== 0
  ) {
    onChangeListStage(true, filteredLists.value[0].Id);
  }
};

const onOpen = async () => {
  try {
    // existLists.value = props.addedLists;
    console.log(props.entityType)
    fileId.value = props.id;
    await onFetchLists();
    isListInit.value = true;
    await nextTick(() => $input.value?.focusInput());
    // console.log(
    //   "listOfElement",
    //   await AnalyticsApi.getListOfElement(props.fileId),
    // );
  } catch (e) {
    console.error(e);
  }
};

const onSearch = async () => {
  $body.value?.scrollTo({
    top: 0,
  });
  canLoadNext.value = true;
  skip.value = 0;
  await onFetchLists();
};

const debouncedSearch = debounce(onSearch, 500);

const onFetchLists = async () => {
  try {
    if (canLoadNext.value) {
      isLoading.value = true;
      const listsArray = await AnalyticsApi.getList(
        filter.value,
        0,
        props.entityType,
        fileId.value as number,
      );
      lists.value = [];
      lists.value =
        skip.value === 0
          ? listsArray.Lists
          : lists.value
          ? [...lists.value, ...listsArray.Lists]
          : listsArray.Lists;
      skip.value += take;
    }
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

const onAddElementToList = async (listId: number) => {
  try {
    isLoading.value = true;
    await AnalyticsApi.addElementToList(listId, '', props.entityType, props.id);
    // existLists.value.
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

const onAddElementToNewList = async (listId: number) => {
  try {
    isLoading.value = true;
    await AnalyticsApi.addElementToList(listId, filter.value, props.entityType, props.id);
    emit("addList");
    await onFetchLists();
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

const onDeleteFileFromList = async (listId: number) => {
  try {
    isLoading.value = true;
    await AnalyticsApi.removeElementIntoList(listId, props.entityType, props.id);
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

const dropAllLists = async () => {
  try {
    isLoading.value = true;
    await detachAllClientsFromContact(
      fileId.value as number,
      existLists.value.map((client) => client.Id),
      existLists.value[0].Id,
    );

    existLists.value.splice(0);
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};
const detachAllClientsFromContact = async (
  fileId: number,
  listIds: number[],
  lastExistClientId: number,
) => {
  try {
    await Promise.allSettled(
      listIds.map((id) => AnalyticsApi.removeElementIntoList(id, props.entityType, fileId)),
    );
  } catch (e) {
    console.error(e);
  }
};

const isListAdded = (clientId: number) => {
  return !!existLists.value.find((client) => client.Id === clientId);
};

const onChangeListStage = async (stage: boolean, clientId: number) => {
  if (lists.value) {
    if (stage) {
      await onAddElementToList(clientId);
      existLists.value.push(
        lists.value.find((client) => client.Id === clientId)!,
      );
    } else {
      await onDeleteFileFromList(clientId);
      existLists.value.splice(
        existLists.value.findIndex((client) => client.Id === clientId),
        1,
      );
    }
  }
};

const onChangeClientStageFromName = async (fileId: number) => {
  if (lists.value) {
    if (existLists.value.find((client) => client.Id === fileId)) {
      await onDeleteFileFromList(fileId);
      existLists.value.splice(
        existLists.value.findIndex((client) => client.Id === fileId),
        1,
      );
    } else {
      await onAddElementToList(fileId);
      existLists.value.push(
        lists.value.find((client) => client.Id === fileId)!,
      );
    }
  }
};

watch(filter, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    debouncedSearch();
  }
});

watch(isListInit, (newValue) => {
  if (newValue) {
    nextTick(() => {
      $body.value?.addEventListener("scroll", async () => {
        if (canLoadNext.value && !isLoading.value) {
          if (
            $body.value?.scrollTop! + $body.value?.offsetHeight! >
            $body.value?.scrollHeight! - 500
          ) {
            await onFetchLists();
          }
        }
      });
    });
  }
});

onMounted(() => onOpen());
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.add-client-modal {
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  padding: 12px 0 0px 12px;

  &__search {
    display: flex;
    gap: 8px;
    width: 100%;
    margin-bottom: 8px;
    padding-right: 12px;

    &-input {
      width: 100%;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    padding-right: 12px;
    color: #979797;

    &-title {
      font-size: 12px;
      line-height: 16px;
      font-weight: 600;
    }

    &-drop-btn {
      font-size: 14px;
      line-height: 18px;
      cursor: pointer;

      &:hover {
        color: #396742;
        text-decoration: underline;
      }
    }
  }

  &__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 8px;
    overflow: auto;

    &-item {
      display: flex;
      font-size: 14px;
      line-height: 18px;
      color: $color-text_main;
      cursor: pointer;
      transition: color 0.1s;

      &-text {
        color: $color-text_main;
        font-size: 14px;
        line-height: 18px;
      }
    }

    &-empty {
      flex-direction: column;
      height: 100%;
      display: flex;
      justify-content: center;
      text-align: center;
    }
  }
}

.highlight {
  background-color: #d3e3fd;
  border-radius: 4px;
  padding: 1px 3px;
}
</style>
